import { createApp } from '@backstage/frontend-defaults';
import {
  microsoftAuthApiRef,
  googleAuthApiRef,
  OpenIdConnectApi,
} from '@backstage/core-plugin-api';
import { SignInPage } from '@backstage/core-components';
import LightIcon from '@material-ui/icons/WbSunny';
import { veluxTheme } from './theme';
import { UnifiedThemeProvider } from '@backstage/theme';
import { convertLegacyPlugin } from '@backstage/core-compat-api';
import {
  ApiBlueprint,
  configApiRef,
  createFrontendModule,
} from '@backstage/frontend-plugin-api';
import {
  IconBundleBlueprint,
  SignInPageBlueprint,
  ThemeBlueprint,
} from '@backstage/plugin-app-react';
import { navModule } from './modules/nav';
import { SysdigPage, sysdigPlugin } from '@sysdig/backstage-plugin-sysdig';
import { convertLegacyEntityContentExtension } from '@backstage/plugin-catalog-react/alpha';
import { githubPullRequestsPlugin } from '@roadiehq/backstage-plugin-github-pull-requests';
import {
  kubernetesAuthProvidersApiRef,
  KubernetesAuthProviders,
} from '@backstage/plugin-kubernetes-react';
import {
  AzureIcon,
  GrafanaIcon,
  HarborIcon,
  JfrogIcon,
  SysdigIcon,
} from './components/home/icons';
import LogoIcon from './components/Root/LogoIcon';
import { homeModule } from './modules/home/homeModule';

const signInPage = SignInPageBlueprint.make({
  params: {
    loader: async () => props =>
      (
        <SignInPage
          {...props}
          auto
          provider={{
            id: 'azure-auth-provider',
            title: 'Azure',
            message: 'Sign in using Azure',
            apiRef: microsoftAuthApiRef,
          }}
        />
      ),
  },
});

export const customVeluxThemeExtension: any = ThemeBlueprint.make({
  name: 'VELUX Theme',
  params: {
    theme: {
      id: 'velux-theme',
      title: 'VELUX theme',
      variant: 'light',
      icon: <LightIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={veluxTheme} children={children} />
      ),
    },
  },
});

// Add support for sysdig plugin in the frontend system.
// The plugin should provide support for the new frontend system natively in the future.
const convertedSysdigPlugin = convertLegacyPlugin(sysdigPlugin, {
  extensions: [
    convertLegacyEntityContentExtension(SysdigPage, {
      name: 'sysdig',
      path: '/sysdig',
      title: 'Sysdig',
      filter: 'kind:Component',
    }),
  ],
});

// Add support for policy reporter plugin in the frontend system.
// The plugin should provide support for the new frontend system natively in the future.
const convertedGithubPR = convertLegacyPlugin(githubPullRequestsPlugin, {
  extensions: [],
});

const kubernetesAuthModule = createFrontendModule({
  pluginId: 'kubernetes',
  extensions: [
    ApiBlueprint.make({
      name: 'kubernetes-auth-providers',
      params: defineParams =>
        defineParams({
          api: kubernetesAuthProvidersApiRef,
          deps: {
            microsoftAuthApi: microsoftAuthApiRef,
            googleAuthApi: googleAuthApiRef,
            configApi: configApiRef,
          },
          factory: ({ microsoftAuthApi, googleAuthApi, configApi }) => {
            const scope = configApi.getString(
              'kubernetes.oidcTokenProviders.microsoft.scope',
            );
            return new KubernetesAuthProviders({
              microsoftAuthApi,
              googleAuthApi,
              oidcProviders: {
                microsoft: {
                  getIdToken: async () => {
                    return microsoftAuthApi.getAccessToken(scope);
                  },
                } as OpenIdConnectApi,
              },
            });
          },
        }),
    }),
  ],
});

const extraIcons = IconBundleBlueprint.make({
  name: 'extra-icons',
  params: {
    icons: {
      harbor: <HarborIcon />,
      azure: <AzureIcon />,
      grafana: <GrafanaIcon />,
      jfrog: <JfrogIcon />,
      sysdig: <SysdigIcon />,
      logo: <LogoIcon />,
    },
  },
});

const app = createApp({
  features: [
    createFrontendModule({
      pluginId: 'app',
      extensions: [customVeluxThemeExtension, signInPage, extraIcons],
    }),
    homeModule,
    navModule,
    convertedSysdigPlugin,
    convertedGithubPR,
    kubernetesAuthModule,
  ],
});

export default app.createRoot();
